import {
  Close,
  CloudUpload,
  DescriptionOutlined,
  ImageOutlined,
  InsertDriveFileOutlined,
  PictureAsPdfOutlined,
  TableChartOutlined,
  FolderZipOutlined,
  UploadFile,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useState, useCallback, useRef } from "react";
import { Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { convertSizeInReadable } from "../../utils/sizeDecoder";

import "./styles/customUpload.scss";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const getFileIcon = (extension) => {
  const ext = extension?.toLowerCase();
  if (["png", "jpg", "jpeg", "gif", "svg", "webp"].includes(ext)) return <ImageOutlined sx={{ color: 'var(--upload-primary)' }} />;
  if (ext === "pdf") return <PictureAsPdfOutlined sx={{ color: '#ef4444' }} />;
  if (["xls", "xlsx", "csv"].includes(ext)) return <TableChartOutlined sx={{ color: '#10b981' }} />;
  if (["doc", "docx"].includes(ext)) return <DescriptionOutlined sx={{ color: 'var(--upload-primary)' }} />;
  if (["zip", "rar", "7z"].includes(ext)) return <FolderZipOutlined sx={{ color: '#f59e0b' }} />;
  return <InsertDriveFileOutlined color="action" />;
};

const SelectedFiles = ({ field, removeFile, size = "large" }) => {
  if (size === "small") {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {field.value.map((file, i) => (
          <Tooltip key={i} title={`${file.name} (${convertSizeInReadable(file.size)})`} arrow>
            <Chip
              label={(function() {
                const sizeStr = convertSizeInReadable(file.size);
                const nameStr = file.name.length > 15 ? `${file.name.slice(0, 12)}...` : file.name;
                return sizeStr ? `${nameStr} (${sizeStr})` : nameStr;
              })()}
              size="small"
              variant="outlined"
              onDelete={() => removeFile(field, file)}
              sx={{ borderRadius: "8px", borderStyle: "dashed", borderColor: "#cbd5e1" }}
            />
          </Tooltip>
        ))}
      </Box>
    );
  }

  return (
    <div className="selected-file-grid">
      {field.value.map((file, i) => {
        const ext = file.name?.split(".").pop();
        return (
          <div key={i} className="file-card">
            <div className="file-icon-box">
              {getFileIcon(ext)}
            </div>
            <div className="file-info">
              <span className="file-name" title={file.name}>{file.name}</span>
              <span className="file-meta">{convertSizeInReadable(file.size)}</span>
            </div>
            <IconButton
              size="small"
              className="file-remove-btn"
              onClick={() => removeFile(field, file)}
            >
              <Close fontSize="small" />
            </IconButton>
          </div>
        );
      })}
    </div>
  );
};

const CustomUpload = (props) => {
  const {
    ref, // React 19: ref is just a prop
    control,
    name,
    rules = {},
    label = undefined,
    isFolder = false,
    extensions = [],
    Icon = CloudUpload,
    iconProps = {},
    iconTittle = "",
    isMultiple = false,
    fileType = null,
    labelSize = "large",
    size = "large",
    renderLabel,
    labelTypographyProps = {},
    maxFiles = 5,
    maxFileSize = 15728640,
    ...others
  } = props;

  const [dragging, setDragging] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const inputFileRef = useRef(null);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  }, []);

  const handleDrop = useCallback(
    async (e, field) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        if (isSelecting) {
          throw new Error("An upload is already in progress.");
        }
        setDragging(false);
        setIsSelecting(true);
        const files = [...e.dataTransfer.files];
        if (!isMultiple && files.length > 1) {
          throw new Error("Only single file upload is allowed.");
        }
        const invalidFiles = files.filter((file) => {
          const extension = file?.name?.split(".").pop()?.toLowerCase();
          return extensions.length > 0 && !extensions.includes(extension);
        });
        if (invalidFiles.length > 0) {
          throw new Error(`Invalid file type(s). Expected: ${extensions.join(", ")}`);
        }
        if (isMultiple && (field.value || []).length + files.length > maxFiles) {
          throw new Error(`You can attach up to ${maxFiles} files only.`);
        }
        const largeFiles = files.filter((file) => file.size > maxFileSize);
        if (largeFiles.length > 0) {
          throw new Error(`Some files are too large. Max size allowed is ${convertSizeInReadable(maxFileSize)}.`);
        }
        const updatedFiles = isMultiple ? [...(field.value || []), ...files] : files;
        field.onChange(updatedFiles);
      } catch (err) {
        toast.error(err?.message || "Error dropping files.");
      } finally {
        setIsSelecting(false);
      }
    },
    [extensions, isSelecting, isMultiple, maxFiles, maxFileSize]
  );

  const removeFile = useCallback(
    (field, fileToRemove) => {
      const currentFiles = field.value || [];
      const updatedFiles = currentFiles.filter((file) => file !== fileToRemove);
      field.onChange(updatedFiles);
      if (inputFileRef.current) inputFileRef.current.value = "";
    },
    []
  );

  const handleInputChange = useCallback(
    (e, field) => {
      try {
        e.stopPropagation();
        setIsSelecting(true);
        const files = [...(e?.target?.files || [])];
        if (files.length > 0) {
          const invalidFiles = files.filter((file) => {
            const extension = file?.name?.split(".").pop()?.toLowerCase();
            return extensions.length > 0 && !extensions.includes(extension);
          });
          if (invalidFiles.length > 0) {
            throw new Error(`Invalid file type(s). Expected: ${extensions.join(", ")}`);
          }
          if (isMultiple && (field.value || []).length + files.length > maxFiles) {
            throw new Error(`You can attach up to ${maxFiles} files only.`);
          }
          const largeFiles = files.filter((file) => file.size > maxFileSize);
          if (largeFiles.length > 0) {
            throw new Error(`Some files are too large. Max size allowed is ${convertSizeInReadable(maxFileSize)}.`);
          }
          const updatedFiles = isMultiple ? [...(field.value || []), ...files] : files;
          field.onChange(updatedFiles);
        }
      } catch (err) {
        toast.error(err?.message || "Error selecting files.");
      } finally {
        setIsSelecting(false);
      }
    },
    [extensions, isMultiple, maxFiles, maxFileSize]
  );

  const getContainerClassNames = useCallback(
    (error) => {
      return `dragContainer ${dragging ? "active" : ""} ${others.className || ""} ${error ? "error" : ""} ${labelSize === "small" ? "small" : ""} `;
    },
    [dragging, others?.className, labelSize]
  );

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { ref: hookRef, ...field }, fieldState: { error } }) => (
        <Box sx={{ width: '100%' }}>
          {renderLabel?.()}
          
          <button
            ref={ref || hookRef}
            type="button"
            className={getContainerClassNames(error)}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, field)}
            onClick={() => inputFileRef.current?.click()}
          >
            <div className="inner-box">
              <div className="file-upload-circle">
                <div className="icon-inner">
                  {dragging ? (
                    <CloudUpload sx={{ fontSize: '1.4rem' }} />
                  ) : (
                    <UploadFile sx={{ fontSize: '1.4rem', color: 'white' }} />
                  )}
                </div>
              </div>

              <div className="label-container">
                <Typography component="div" sx={{ color: 'var(--upload-text)', fontWeight: 700, fontSize: '1.05rem', mt: 1, mb: 0.25 }}>
                  Drop your file here
                </Typography>
                <Typography component="div" sx={{ color: 'var(--upload-text-muted)', fontSize: '0.82rem', fontWeight: 500 }}>
                  or <span style={{ color: 'var(--upload-primary)', fontWeight: 600, cursor: 'pointer' }}>browse files</span> on your device
                </Typography>
                {extensions.length > 0 && (
                  <Typography variant="caption" sx={{ color: 'var(--upload-text-muted)', mt: 1, display: 'block', opacity: 0.7, fontSize: '0.75rem' }}>
                    Supported: {extensions.join(", ").toUpperCase()}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ color: 'var(--upload-text-muted)', mt: 0.5, display: 'block', opacity: 0.7, fontSize: '0.75rem' }}>
                    Currently max size is {convertSizeInReadable(maxFileSize)}.
                </Typography>
              </div>

              <VisuallyHiddenInput
                type="file"
                ref={inputFileRef}
                accept={extensions.length > 0 ? extensions.map((ext) => `.${ext}`).join(",") : "*"}
                multiple={isMultiple}
                onChange={(e) => handleInputChange(e, field)}
              />

              {isSelecting && <CircularProgress size={24} sx={{ mt: 1 }} />}
            </div>
          </button>

          {Array.isArray(field?.value) && field.value.length > 0 && (
            fileType === "image" ? (
              <div className="selected-file-images">
                {field.value.map((file, i) => (
                  <div className="image-preview-card" key={i}>
                    <img src={URL.createObjectURL(file)} alt={file.name} />
                    <div className="img-overlay">
                      <div className="img-info-box">
                        <Typography variant="caption" className="img-name">{file.name}</Typography>
                        <Typography variant="caption" className="img-size">{convertSizeInReadable(file.size) || '0 KB'}</Typography>
                      </div>
                      <IconButton 
                        size="small" 
                        className="img-remove-btn"
                        onClick={(e) => { e.stopPropagation(); removeFile(field, file); }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <SelectedFiles field={field} removeFile={removeFile} size={size} />
            )
          )}

          {error && (
            <Typography color="error" variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              {error.message}
            </Typography>
          )}
        </Box>
      )}
    />
  );
};

CustomUpload.propTypes = {
  control: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  rules: PropTypes.object,
  label: PropTypes.string,
  isFolder: PropTypes.bool,
  extensions: PropTypes.arrayOf(PropTypes.string),
  Icon: PropTypes.elementType,
  iconProps: PropTypes.object,
  iconTittle: PropTypes.string,
  fileType: PropTypes.oneOf(["image", null]),
  maxFiles: PropTypes.number,
  maxFileSize: PropTypes.number,
};

export default CustomUpload;
